<script setup lang="ts">
import { computed, ref, nextTick, watch } from 'vue';
import { marked } from 'marked';
import DOMPurify from 'dompurify';
import { useChat } from '@/composables/useChat';
import type { ContextoAssistente } from '@/types/api';

// Markdown -> HTML sanitizado. Setup global do marked: GFM (tabelas, listas
// task), breaks=true (quebra de linha vira <br>), e cada link abre em
// nova aba (target="_blank" + rel="noopener noreferrer" pra seguranca).
marked.setOptions({ gfm: true, breaks: true });

// Renderer customizado pra forcar target=_blank em links — assim o usuario
// nao perde o chat ao clicar em manual do Drive.
const renderer = new marked.Renderer();
const linkBaseRender = renderer.link.bind(renderer);
renderer.link = (href, title, text) => {
  const html = linkBaseRender(href, title, text);
  return html.replace(/^<a /, '<a target="_blank" rel="noopener noreferrer" ');
};

function renderMarkdown(texto: string): string {
  if (!texto) return '';
  const rawHtml = marked.parse(texto, { renderer, async: false }) as string;
  // DOMPurify previne XSS — LLM pode (em teoria) gerar tags maliciosas
  // via prompt injection. Whitelist: tags e attrs comuns de markdown.
  return DOMPurify.sanitize(rawHtml, {
    ALLOWED_TAGS: [
      'a', 'p', 'br', 'strong', 'em', 'b', 'i', 'u', 'code', 'pre',
      'ul', 'ol', 'li', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
      'hr', 'blockquote', 'table', 'thead', 'tbody', 'tr', 'th', 'td', 'span'
    ],
    ALLOWED_ATTR: ['href', 'title', 'target', 'rel', 'class']
  });
}

const props = defineProps<{
  apiBaseUrl: string;
  contexto: ContextoAssistente;
}>();

const aberto = ref(false);
const inputTexto = ref('');
const listaRef = ref<HTMLDivElement | null>(null);

const chat = useChat({
  apiBaseUrl: props.apiBaseUrl,
  contexto: props.contexto
});

// ehAdmin = true APENAS pra UserID = 2 (definido no _Layout.cshtml legado).
// TipoID = -7 sozinho NAO autoriza corrigir — esses sao "leitura apenas".
const podeCorrigir = computed(() => props.contexto.ehAdmin === true);

// Set de mensagem-IDs em estado "copiado" (mostra "Copiado" por 2s).
// Usar ref<Set<...>> + reatribuicao porque Vue 3 nao reage a mutacoes
// de Set/Map sem trigger explicito.
const mensagensCopiadas = ref<Set<string>>(new Set());

async function copiarMensagem(mensagemId: string, texto: string) {
  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(texto);
    } else {
      // Fallback p/ http (sem clipboard API): textarea oculta + execCommand
      const ta = document.createElement('textarea');
      ta.value = texto;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
    }
    const proximo = new Set(mensagensCopiadas.value);
    proximo.add(mensagemId);
    mensagensCopiadas.value = proximo;
    setTimeout(() => {
      const limpo = new Set(mensagensCopiadas.value);
      limpo.delete(mensagemId);
      mensagensCopiadas.value = limpo;
    }, 2000);
  } catch (e) {
    console.error('Falha ao copiar mensagem:', e);
  }
}

// Estado do modal de correcao
const correcaoMensagemId = ref<string | null>(null);
const correcaoTexto = ref('');
const correcaoEnviando = ref(false);
const correcaoErro = ref<string | null>(null);

function abrirCorrecao(mensagemId: string) {
  correcaoMensagemId.value = mensagemId;
  correcaoTexto.value = '';
  correcaoErro.value = null;
}

function fecharCorrecao() {
  correcaoMensagemId.value = null;
  correcaoTexto.value = '';
  correcaoErro.value = null;
}

async function enviarCorrecao() {
  if (!correcaoMensagemId.value) return;
  if (!correcaoTexto.value.trim()) {
    correcaoErro.value = 'Escreva a correcao antes de enviar.';
    return;
  }
  correcaoEnviando.value = true;
  correcaoErro.value = null;
  const r = await chat.corrigir(correcaoMensagemId.value, correcaoTexto.value);
  correcaoEnviando.value = false;
  if (r.ok) fecharCorrecao();
  else correcaoErro.value = r.erro ?? 'Falha ao enviar.';
}

watch(() => chat.mensagens.value.length, async () => {
  await nextTick();
  if (listaRef.value) listaRef.value.scrollTop = listaRef.value.scrollHeight;
});

async function enviar() {
  const texto = inputTexto.value.trim();
  if (!texto || chat.carregando.value) return;
  inputTexto.value = '';
  await chat.perguntar(texto);
}

function tecla(e: KeyboardEvent) {
  if (e.key === 'Enter' && !e.shiftKey) {
    e.preventDefault();
    enviar();
  }
}

function rotuloOrigem(origem?: string) {
  switch (origem) {
    case 'cache_exato': return 'Cache (exato)';
    case 'cache_semantico': return 'Cache (semântico)';
    case 'llm_haiku': return 'Haiku';
    case 'llm_sonnet': return 'Sonnet';
    case 'fallback_busca': return 'Busca';
    default: return '';
  }
}
</script>

<template>
  <div class="ia-root">
    <button
      v-if="!aberto"
      class="ia-bubble"
      title="Assistente IA"
      @click="aberto = true">
      <span class="ia-bubble-icon">💬</span>
    </button>

    <div v-else class="ia-painel" role="dialog" aria-label="Assistente IA do MaisVidas">
      <header class="ia-header">
        <div class="ia-header-titulo">
          <strong>Assistente</strong>
          <span class="ia-header-sub">MaisVidas</span>
        </div>
        <button class="ia-header-close" @click="aberto = false" aria-label="Fechar">×</button>
      </header>

      <div ref="listaRef" class="ia-mensagens">
        <div v-if="chat.mensagens.value.length === 0" class="ia-vazio">
          <p>Olá! Como posso ajudar você hoje?</p>
          <p class="ia-vazio-dica">Posso explicar como funcionam as telas, conceitos do sistema e onde encontrar relatórios.</p>
        </div>

        <div
          v-for="m in chat.mensagens.value"
          :key="m.id"
          :class="['ia-msg', m.papel === 'user' ? 'ia-msg-user' : 'ia-msg-assistant']">
          <div v-if="m.carregando && m.textoStatus" class="ia-pensando ia-status">
            {{ m.textoStatus }}
          </div>
          <div v-else-if="m.carregando" class="ia-pensando">
            <span></span><span></span><span></span>
          </div>

          <div v-else-if="m.erro" class="ia-erro">
            ⚠️ {{ m.erro }}
          </div>

          <div v-else>
            <!-- User messages: texto puro (sem markdown rendering, evita surpresas).
                 Assistant messages: markdown renderizado + sanitizado pra HTML. -->
            <div
              v-if="m.papel === 'user'"
              class="ia-msg-texto">{{ m.texto }}</div>
            <div
              v-else
              class="ia-msg-texto ia-md"
              v-html="renderMarkdown(m.texto)"></div>

            <div v-if="m.papel === 'assistant' && !m.carregando" class="ia-msg-meta">
              <span v-if="m.origemResposta" class="ia-origem">{{ rotuloOrigem(m.origemResposta) }}</span>
              <button
                class="ia-copiar"
                :title="mensagensCopiadas.has(m.id) ? 'Copiado!' : 'Copiar resposta'"
                @click="copiarMensagem(m.id, m.texto)">
                {{ mensagensCopiadas.has(m.id) ? '✓ Copiado' : '📋 Copiar' }}
              </button>
              <button
                :class="['ia-feedback', m.feedback === 1 ? 'ativo' : '']"
                title="Resposta útil"
                @click="chat.darFeedback(m.id, 1)">👍</button>
              <button
                :class="['ia-feedback', m.feedback === 2 ? 'ativo' : '']"
                title="Resposta não ajudou"
                @click="chat.darFeedback(m.id, 2)">👎</button>
              <button
                v-if="podeCorrigir && !m.correcaoEnviada"
                class="ia-corrigir"
                title="Corrigir esta resposta (suporte)"
                @click="abrirCorrecao(m.id)">📝 Corrigir</button>
              <span v-if="m.correcaoEnviada && m.correcaoAutoAprovada" class="ia-correcao-aprovada" title="Correção aplicada na base de conhecimento">✓ correção aplicada</span>
              <span v-else-if="m.correcaoEnviada" class="ia-correcao-enviada" title="Correção em revisão">✓ correção enviada para revisão</span>
            </div>
          </div>
        </div>
      </div>

      <footer class="ia-footer">
        <textarea
          v-model="inputTexto"
          rows="2"
          placeholder="Pergunte sobre o sistema..."
          :disabled="chat.carregando.value"
          @keydown="tecla"></textarea>
        <button
          class="ia-enviar"
          :disabled="chat.carregando.value || !inputTexto.trim()"
          @click="enviar">
          Enviar
        </button>
      </footer>
    </div>

    <!-- Modal de correção (apenas usuários TipoID = -7) -->
    <div v-if="correcaoMensagemId" class="ia-overlay" @click.self="fecharCorrecao">
      <div class="ia-modal-corrigir">
        <header class="ia-modal-header">
          <strong>Corrigir resposta</strong>
          <button class="ia-modal-close" @click="fecharCorrecao" aria-label="Fechar">×</button>
        </header>
        <div class="ia-modal-body">
          <p class="ia-modal-help">
            Sua correção será revisada e, se aprovada, vira chunk na base de conhecimento.
            Escreva a versão correta da resposta — passos, caminhos de menu, regras, etc.
          </p>
          <textarea
            v-model="correcaoTexto"
            rows="8"
            placeholder="Escreva aqui a resposta correta..."
            :disabled="correcaoEnviando"></textarea>
          <div v-if="correcaoErro" class="ia-modal-erro">⚠️ {{ correcaoErro }}</div>
        </div>
        <footer class="ia-modal-footer">
          <button class="ia-btn-cancelar" :disabled="correcaoEnviando" @click="fecharCorrecao">Cancelar</button>
          <button class="ia-btn-enviar" :disabled="correcaoEnviando || !correcaoTexto.trim()" @click="enviarCorrecao">
            {{ correcaoEnviando ? 'Enviando...' : 'Enviar correção' }}
          </button>
        </footer>
      </div>
    </div>
  </div>
</template>

<style scoped>
.ia-root { position: fixed; bottom: 24px; right: 24px; z-index: 99999; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; font-size: 14px; }

.ia-bubble {
  width: 56px; height: 56px; border-radius: 50%; border: none; cursor: pointer;
  background: linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%);
  color: white; box-shadow: 0 4px 12px rgba(0,0,0,0.15); display: flex; align-items: center; justify-content: center;
  transition: transform 0.15s;
}
.ia-bubble:hover { transform: scale(1.05); }
.ia-bubble-icon { font-size: 24px; }

.ia-painel {
  width: 380px; height: 560px; max-height: calc(100vh - 48px);
  background: white; border-radius: 12px; box-shadow: 0 10px 40px rgba(0,0,0,0.2);
  display: flex; flex-direction: column; overflow: hidden;
}

.ia-header {
  background: linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%);
  color: white; padding: 14px 16px; display: flex; align-items: center; justify-content: space-between;
}
.ia-header-titulo { display: flex; flex-direction: column; line-height: 1.1; }
.ia-header-sub { font-size: 11px; opacity: 0.85; }
.ia-header-close { background: transparent; border: none; color: white; font-size: 24px; cursor: pointer; padding: 0 4px; }

.ia-mensagens { flex: 1; overflow-y: auto; padding: 12px 14px; background: #f9fafb; }

.ia-vazio { text-align: center; color: #6b7280; margin-top: 40px; }
.ia-vazio-dica { font-size: 12px; margin-top: 8px; }

.ia-msg { margin-bottom: 14px; max-width: 88%; }
.ia-msg-user { margin-left: auto; }
.ia-msg-user .ia-msg-texto { background: #4f46e5; color: white; padding: 9px 13px; border-radius: 14px 14px 3px 14px; line-height: 1.45; white-space: pre-wrap; word-wrap: break-word; }
.ia-msg-assistant .ia-msg-texto { background: white; color: #1f2937; padding: 11px 14px; border-radius: 14px 14px 14px 3px; box-shadow: 0 1px 3px rgba(0,0,0,0.08); line-height: 1.55; word-wrap: break-word; }

/* ============== Markdown rendering (assistant messages) ==============
   IMPORTANTE: o conteudo e' injetado via v-html (renderMarkdown), entao os
   elementos (h1, p, a...) NAO recebem o atributo de scope do <style scoped>.
   Por isso TODOS os seletores de filhos usam :deep() — sem isso o CSS nao
   se aplica e headings renderizam no tamanho default do browser (~28px),
   estourando a janelinha do chat. */
.ia-md :deep(p) { margin: 0 0 8px 0; font-size: 14px; }
.ia-md :deep(p:last-child) { margin-bottom: 0; }
.ia-md :deep(strong) { font-weight: 600; color: #111827; }
.ia-md :deep(em) { font-style: italic; }
.ia-md :deep(a) {
  color: #4f46e5; text-decoration: none; font-weight: 500;
  border-bottom: 1px solid #c7d2fe; transition: color 0.15s, border-color 0.15s;
  word-break: break-word;
}
.ia-md :deep(a:hover) { color: #3730a3; border-bottom-color: #4f46e5; }
.ia-md :deep(ul), .ia-md :deep(ol) { margin: 6px 0 8px 0; padding-left: 22px; }
.ia-md :deep(ul) { list-style-type: disc; }
.ia-md :deep(ol) { list-style-type: decimal; }
.ia-md :deep(li) { margin-bottom: 3px; font-size: 14px; }
.ia-md :deep(li:last-child) { margin-bottom: 0; }
.ia-md :deep(li > p) { margin-bottom: 4px; }
/* Headings em chat conversacional: nao competem com o texto principal.
   Mesmo tamanho do corpo (14px), so' o peso muda. Sem !important o
   default do browser (h1=2em) prevalece em alguns casos de cascata. */
.ia-md :deep(h1), .ia-md :deep(h2), .ia-md :deep(h3),
.ia-md :deep(h4), .ia-md :deep(h5), .ia-md :deep(h6) {
  margin: 8px 0 4px 0; color: #111827; line-height: 1.4;
  font-size: 14px !important;
}
.ia-md :deep(h1), .ia-md :deep(h2) { font-weight: 700; }
.ia-md :deep(h3), .ia-md :deep(h4),
.ia-md :deep(h5), .ia-md :deep(h6) { font-weight: 600; }
.ia-md :deep(h1:first-child), .ia-md :deep(h2:first-child),
.ia-md :deep(h3:first-child), .ia-md :deep(h4:first-child),
.ia-md :deep(h5:first-child), .ia-md :deep(h6:first-child) { margin-top: 0; }
.ia-md :deep(hr) {
  border: none; border-top: 1px solid #e5e7eb;
  margin: 12px 0 10px 0;
}
.ia-md :deep(code) {
  background: #f3f4f6; color: #b45309;
  padding: 1px 5px; border-radius: 3px;
  font-family: ui-monospace, SFMono-Regular, Consolas, monospace;
  font-size: 12.5px;
}
.ia-md :deep(pre) {
  background: #1f2937; color: #e5e7eb;
  padding: 10px 12px; border-radius: 6px;
  overflow-x: auto; margin: 8px 0;
  font-size: 12px; line-height: 1.45;
}
.ia-md :deep(pre code) { background: transparent; color: inherit; padding: 0; }
.ia-md :deep(blockquote) {
  border-left: 3px solid #c7d2fe; margin: 8px 0;
  padding: 4px 0 4px 12px; color: #4b5563; font-size: 13px;
}
.ia-md :deep(table) {
  border-collapse: collapse; margin: 8px 0; font-size: 12.5px;
  width: 100%;
}
.ia-md :deep(th), .ia-md :deep(td) {
  border: 1px solid #e5e7eb; padding: 5px 8px; text-align: left;
}
.ia-md :deep(th) { background: #f9fafb; font-weight: 600; }

.ia-msg-meta { margin-top: 4px; display: flex; gap: 6px; align-items: center; font-size: 11px; }
.ia-origem { color: #6b7280; flex: 1; }
.ia-feedback { background: transparent; border: 1px solid #e5e7eb; border-radius: 4px; padding: 2px 6px; cursor: pointer; }
.ia-feedback.ativo { background: #eef2ff; border-color: #4f46e5; }

.ia-pensando { display: flex; gap: 4px; padding: 12px; }
.ia-pensando span { width: 6px; height: 6px; background: #9ca3af; border-radius: 50%; animation: pulse 1.4s infinite; }
.ia-pensando span:nth-child(2) { animation-delay: 0.2s; }
.ia-pensando span:nth-child(3) { animation-delay: 0.4s; }
@keyframes pulse { 0%,80%,100%{opacity:0.3;} 40%{opacity:1;} }

/* Passo em andamento com texto (ex.: consulta de dados rodando no +Vidas).
   Herda o padding do .ia-pensando, mas sem os pontinhos, que sao spans. */
.ia-status { color: #6b7280; font-size: 13px; font-style: italic; }

.ia-erro { color: #b91c1c; padding: 8px 12px; background: #fee2e2; border-radius: 8px; font-size: 13px; }

.ia-footer { padding: 10px; border-top: 1px solid #e5e7eb; display: flex; gap: 8px; background: white; }
.ia-footer textarea { flex: 1; resize: none; border: 1px solid #d1d5db; border-radius: 6px; padding: 8px; font-family: inherit; font-size: 14px; outline: none; }
.ia-footer textarea:focus { border-color: #4f46e5; }
.ia-enviar { background: #4f46e5; color: white; border: none; border-radius: 6px; padding: 8px 16px; cursor: pointer; font-weight: 600; }
.ia-enviar:disabled { opacity: 0.5; cursor: not-allowed; }

.ia-corrigir { background: transparent; border: 1px solid #fcd34d; color: #b45309; border-radius: 4px; padding: 2px 6px; cursor: pointer; font-size: 11px; }
.ia-corrigir:hover { background: #fef3c7; }
.ia-copiar { background: transparent; border: 1px solid #e5e7eb; color: #4b5563; border-radius: 4px; padding: 2px 6px; cursor: pointer; font-size: 11px; transition: all 0.15s; }
.ia-copiar:hover { background: #f3f4f6; border-color: #9ca3af; }
.ia-correcao-enviada { color: #047857; font-size: 11px; padding: 2px 6px; }
.ia-correcao-aprovada { color: #047857; font-size: 11px; padding: 2px 6px; font-weight: 600; }

.ia-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.5); display: flex; align-items: center; justify-content: center; z-index: 100000; }
.ia-modal-corrigir { background: white; border-radius: 12px; width: 90vw; max-width: 560px; max-height: 80vh; display: flex; flex-direction: column; box-shadow: 0 20px 60px rgba(0,0,0,0.3); }
.ia-modal-header { padding: 14px 16px; border-bottom: 1px solid #e5e7eb; display: flex; justify-content: space-between; align-items: center; }
.ia-modal-close { background: transparent; border: none; font-size: 22px; cursor: pointer; color: #6b7280; padding: 0 6px; }
.ia-modal-body { padding: 14px 16px; flex: 1; overflow-y: auto; }
.ia-modal-help { font-size: 13px; color: #6b7280; margin: 0 0 10px 0; line-height: 1.4; }
.ia-modal-body textarea { width: 100%; resize: vertical; border: 1px solid #d1d5db; border-radius: 6px; padding: 10px; font-family: inherit; font-size: 14px; outline: none; box-sizing: border-box; }
.ia-modal-body textarea:focus { border-color: #4f46e5; }
.ia-modal-erro { margin-top: 8px; padding: 8px; background: #fee2e2; color: #b91c1c; border-radius: 6px; font-size: 13px; }
.ia-modal-footer { padding: 10px 16px; border-top: 1px solid #e5e7eb; display: flex; gap: 8px; justify-content: flex-end; }
.ia-btn-cancelar { background: white; border: 1px solid #d1d5db; color: #374151; padding: 8px 14px; border-radius: 6px; cursor: pointer; }
.ia-btn-enviar { background: #4f46e5; color: white; border: none; padding: 8px 14px; border-radius: 6px; cursor: pointer; font-weight: 600; }
.ia-btn-enviar:disabled { opacity: 0.5; cursor: not-allowed; }
</style>
